import { useState, useEffect } from "react";
import { 
  Save, 
  Loader2, 
  Clock, 
  Plus, 
  Trash2, 
  Printer, 
  Building2, 
  Truck, 
  Palette, 
  Settings2,
  ChevronRight,
  Monitor,
  Bell,
  CheckCircle2,
  Zap,
  Beef,
  Store,
  Upload,
  Shield
} from "lucide-react";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { 
  Button, 
  Badge, 
  CardGlass, 
  SectionHeader 
} from "@/components/ui/PremiumComponents";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import printService from "@/services/PrintService";
import API from "@/utils/api";

const DAYS = [
  { id: "monday", label: "LUNES" },
  { id: "tuesday", label: "MARTES" },
  { id: "wednesday", label: "MIÉRCOLES" },
  { id: "thursday", label: "JUEVES" },
  { id: "friday", label: "VIERNES" },
  { id: "saturday", label: "SÁBADO" },
  { id: "sunday", label: "DOMINGO" },
];

const defaultSchedule = {
  monday: { enabled: true, shifts: [{ open: "12:00", close: "23:00" }] },
  tuesday: { enabled: true, shifts: [{ open: "12:00", close: "23:00" }] },
  wednesday: { enabled: true, shifts: [{ open: "12:00", close: "23:00" }] },
  thursday: { enabled: true, shifts: [{ open: "12:00", close: "23:00" }] },
  friday: { enabled: true, shifts: [{ open: "12:00", close: "23:00" }] },
  saturday: { enabled: true, shifts: [{ open: "12:00", close: "23:00" }] },
  sunday: { enabled: true, shifts: [{ open: "12:00", close: "23:00" }] },
};

export default function AdminSettings() {
  const [config, setConfig] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    report_email: "",
    delivery_radius_km: 10,
    delivery_fee: 3.50,
    tax_rate: 0.10,
    min_order: 10,
    logo: "",
    primary_color: "#FF4757",
    schedule: defaultSchedule,
    require_active_shift: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activePane, setActivePane] = useState("general");

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/config`);
      const data = response.data;
      let schedule = data.schedule || defaultSchedule;
      const migratedSchedule = {};
      Object.keys(schedule).forEach(day => {
        if (schedule[day].shifts) {
          migratedSchedule[day] = schedule[day];
        } else {
          migratedSchedule[day] = {
            enabled: schedule[day].enabled ?? true,
            shifts: [{ open: schedule[day].open || "12:00", close: schedule[day].close || "23:00" }]
          };
        }
      });
      
      // Legacy Branding Migration
      const primary_color = data.primary_color || data.branding?.primary_color || config.primary_color;
      const logo = data.logo || data.branding?.logo_url || config.logo;

      setConfig({ 
        ...config, 
        ...data, 
        primary_color,
        logo,
        schedule: migratedSchedule 
      });
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/config`, config, { withCredentials: true });
      toast.success("Configuración global sincronizada");
      
      // Auto-refresh to apply new branding to the whole panel (Sidebar, Logo, etc)
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error("Error en la persistencia de datos");
    } finally {
      setIsSaving(false);
    }
  };

  const updateScheduleDay = (day, field, value) => {
    setConfig({ ...config, schedule: { ...config.schedule, [day]: { ...config.schedule[day], [field]: value } } });
  };

  const updateShift = (day, shiftIndex, field, value) => {
    const updatedShifts = [...(config.schedule[day]?.shifts || [])];
    updatedShifts[shiftIndex] = { ...updatedShifts[shiftIndex], [field]: value };
    updateScheduleDay(day, 'shifts', updatedShifts);
  };

  const addShift = (day) => {
    const currentShifts = config.schedule[day]?.shifts || [];
    updateScheduleDay(day, 'shifts', [...currentShifts, { open: "12:00", close: "23:00" }]);
  };

  const removeShift = (day, shiftIndex) => {
    const updatedShifts = config.schedule[day]?.shifts.filter((_, i) => i !== shiftIndex);
    updateScheduleDay(day, 'shifts', updatedShifts.length > 0 ? updatedShifts : [{ open: "12:00", close: "23:00" }]);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Basic validation
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten archivos de imagen");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const uploadToast = toast.loading("Subiendo logotipo...");
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      
      const logoUrl = response.data.url;
      setConfig({ ...config, logo: logoUrl });
      toast.success("Logotipo cargado correctamente", { id: uploadToast });
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error al subir la imagen", { id: uploadToast });
    }
  };

  const panes = [
    { id: "general", label: "Restaurante", icon: <Building2 className="w-4 h-4" /> },
    { id: "orders", label: "Pedidos & Delivery", icon: <Truck className="w-4 h-4" /> },
    { id: "schedule", label: "Horarios", icon: <Clock className="w-4 h-4" /> },
    { id: "branding", label: "Identidad Visual", icon: <Palette className="w-4 h-4" /> },
    { id: "features", label: "Módulos", icon: <Zap className="w-4 h-4" /> },
    { id: "printing", label: "Impresión Local", icon: <Printer className="w-4 h-4" /> },
  ];

  if (isLoading) return <AdminLayout title="Configuración"><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-10 h-10 text-primary animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in duration-700">
        
        <SectionHeader 
          title="Consola de Configuración"
          subtitle="Define los parámetros operativos y la identidad visual de tu sucursal."
          actions={
            <Button onClick={handleSave} disabled={isSaving} interaction="glow" className="h-12 px-8 uppercase font-black tracking-tighter shadow-2xl">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Cambios
            </Button>
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mt-10 items-start">
          
          {/* Navigation Pane */}
          <div className="md:sticky md:top-6 space-y-2">
            {panes.map(pane => (
              <button
                key={pane.id}
                onClick={() => setActivePane(pane.id)}
                className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all duration-300 group ${activePane === pane.id ? 'bg-primary border-primary text-white shadow-xl shadow-primary/20' : 'bg-white/5 border-white/5 text-slate-500 hover:bg-white/10 hover:border-white/10'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${activePane === pane.id ? 'bg-white/20' : 'bg-white/5'}`}>{pane.icon}</div>
                  <span className="text-[11px] font-black uppercase tracking-widest">{pane.label}</span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${activePane === pane.id ? 'translate-x-1 opacity-100' : 'opacity-0'}`} />
              </button>
            ))}

            <div className="mt-10 p-6 bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 rounded-3xl">
               <h5 className="text-[10px] font-black text-primary uppercase tracking-widest mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3" /> Sistema Óptimo
               </h5>
               <p className="text-[10px] text-slate-400 leading-relaxed font-bold">
                  Tu configuración está sincronizada con el núcleo de Conexo POS en tiempo real.
               </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="md:col-span-3 space-y-8 animate-in slide-in-from-bottom-4 duration-500">
             
             {activePane === 'general' && (
               <CardGlass title="Identidad Corporativa" subtitle="Datos básicos para facturación y contacto directo.">
                  <div className="space-y-6 pt-6">
                    <div className="space-y-2">
                       <Label className="text-[10px] font-black uppercase text-slate-500">Nombre Público del Restaurante</Label>
                       <Input className="bg-black/40 border-white/10 h-14 text-white font-bold" value={config.name} onChange={(e) => setConfig({ ...config, name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                       <Label className="text-[10px] font-black uppercase text-slate-500">Ubicación Física</Label>
                       <Input className="bg-black/40 border-white/10 h-14 text-white" value={config.address} onChange={(e) => setConfig({ ...config, address: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                       <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-slate-500">Canal de Ventas (Teléfono)</Label>
                          <Input className="bg-black/40 border-white/10 h-14 text-white font-bold" value={config.phone} onChange={(e) => setConfig({ ...config, phone: e.target.value })} />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-slate-500">Email Corporativo</Label>
                          <Input className="bg-black/40 border-white/10 h-14 text-white" value={config.email} onChange={(e) => setConfig({ ...config, email: e.target.value })} />
                       </div>
                    </div>
                    <div className="p-6 bg-white/5 rounded-3xl border border-white/10 flex items-center gap-6">
                       <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center text-primary"><Bell className="w-6 h-6" /></div>
                       <div className="flex-1">
                          <Label className="text-[10px] font-black uppercase text-slate-500 mb-1 block">Email para Reportes Diarios</Label>
                          <Input className="bg-transparent border-none p-0 h-8 text-white text-lg font-black placeholder:text-slate-700 outline-none focus:ring-0" placeholder="reportes@sucursal.es" value={config.report_email || ""} onChange={(e) => setConfig({ ...config, report_email: e.target.value })} />
                       </div>
                    </div>
                  </div>
               </CardGlass>
             )}

             {activePane === 'orders' && (
               <CardGlass title="Lógica de Operaciones" subtitle="Reglas de despacho, impuestos y límites de pedido.">
                  <div className="space-y-8 pt-6">
                    <div className="grid grid-cols-2 gap-8">
                       <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-slate-500">Radio de Cobertura Delivery (km)</Label>
                          <Input type="number" className="bg-black/40 border-white/10 h-14 text-white font-black text-xl" value={config.delivery_radius_km} onChange={(e) => setConfig({ ...config, delivery_radius_km: parseFloat(e.target.value) })} />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-slate-500">Costo Base de Envío (€)</Label>
                          <Input type="number" step="0.10" className="bg-black/40 border-white/10 h-14 text-white font-black text-xl text-primary" value={config.delivery_fee} onChange={(e) => setConfig({ ...config, delivery_fee: parseFloat(e.target.value) })} />
                       </div>
                    </div>

                    <div className="grid grid-cols-2 gap-8">
                       <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-slate-500">Impuesto Aplicable (IVA %)</Label>
                          <Input type="number" className="bg-black/40 border-white/10 h-14 text-white font-bold" value={(config.tax_rate * 100).toFixed(0)} onChange={(e) => setConfig({ ...config, tax_rate: parseFloat(e.target.value) / 100 })} />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase text-slate-500">Ticket Mínimo (€)</Label>
                          <Input type="number" className="bg-black/40 border-white/10 h-14 text-white font-bold" value={config.min_order} onChange={(e) => setConfig({ ...config, min_order: parseFloat(e.target.value) })} />
                       </div>
                    </div>

                    <div className="p-6 bg-primary/5 border border-primary/20 rounded-3xl flex items-center justify-between">
                       <div className="flex flex-col">
                          <span className="text-sm font-black text-white uppercase tracking-tight">Venta Inteligente</span>
                          <span className="text-[10px] text-slate-500 uppercase font-bold">Bloquear pedidos si no hay cajero logueado</span>
                       </div>
                       <Switch checked={config.require_active_shift} onCheckedChange={(val) => setConfig({ ...config, require_active_shift: val })} />
                    </div>
                  </div>
               </CardGlass>
             )}

             {activePane === 'schedule' && (
               <CardGlass title="Cronograma de Apertura" subtitle="Define turnos rotativos y horarios especiales para cada día de la semana.">
                  <div className="space-y-3 mt-6">
                    {DAYS.map((day) => {
                      const daySchedule = config.schedule[day.id] || { enabled: true, shifts: [{ open: "12:00", close: "23:00" }] };
                      const shifts = daySchedule.shifts || [{ open: "12:00", close: "23:00" }];
                      return (
                        <div key={day.id} className="p-5 rounded-[28px] border border-white/5 bg-white/[0.02] flex flex-col md:flex-row md:items-center justify-between gap-6 group hover:bg-white/[0.04] transition-all">
                          <div className="flex items-center gap-6">
                            <Switch checked={daySchedule.enabled ?? true} onCheckedChange={(val) => updateScheduleDay(day.id, "enabled", val)} />
                            <span className={`w-24 text-[11px] font-black tracking-[0.2em] ${!daySchedule.enabled ? 'text-slate-700' : 'text-white'}`}>{day.label}</span>
                          </div>

                          {daySchedule.enabled ? (
                            <div className="flex-1 flex flex-wrap gap-3 items-center">
                              {shifts.map((shift, idx) => (
                                <div key={idx} className="flex items-center gap-2 bg-white/5 p-2 rounded-2xl border border-white/10 animate-in zoom-in-95">
                                  <input type="time" className="bg-transparent text-white font-black text-xs outline-none w-14" value={shift.open || "12:00"} onChange={(e) => updateShift(day.id, idx, "open", e.target.value)} />
                                  <span className="text-slate-600 font-black">-</span>
                                  <input type="time" className="bg-transparent text-white font-black text-xs outline-none w-14" value={shift.close || "23:00"} onChange={(e) => updateShift(day.id, idx, "close", e.target.value)} />
                                  {shifts.length > 1 && <button onClick={() => removeShift(day.id, idx)} className="text-red-500/50 hover:text-red-500 p-1"><Trash2 size={12} /></button>}
                                </div>
                              ))}
                              <Button variant="secondary" size="sm" className="h-8 w-8 p-0 rounded-full" onClick={() => addShift(day.id)}><Plus size={14} /></Button>
                            </div>
                          ) : (
                            <div className="flex-1 text-slate-700 uppercase font-black text-[10px] tracking-tight">Cerrado / Sin Servicio</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
               </CardGlass>
             )}

             {activePane === 'branding' && (
               <CardGlass title="Branding & Estética" subtitle="Sube tu logotipo y elige tu color de marca para la Web y el TPV.">
                  <div className="space-y-8 pt-6">
                       <div className="flex flex-col gap-4">
                          <div className="flex gap-4">
                             <div className="w-24 h-24 bg-white/5 rounded-3xl border border-white/10 flex items-center justify-center overflow-hidden group/logo relative">
                                {config.logo ? (
                                  <img src={config.logo.startsWith('/') ? `${API.replace('/api', '')}${config.logo}` : config.logo} alt="Logo" className="w-full h-full object-contain p-2" />
                                ) : (
                                  <Palette className="w-8 h-8 text-slate-700" />
                                )}
                             </div>
                             <div className="flex-1 space-y-3">
                                <Label className="text-[10px] font-black uppercase text-slate-700">Opciones de Logotipo</Label>
                                <div className="flex gap-2">
                                   <input 
                                     type="file" 
                                     id="logo-upload" 
                                     className="hidden" 
                                     accept="image/*"
                                     onChange={handleLogoUpload}
                                   />
                                   <Button 
                                     variant="secondary" 
                                     className="h-10 text-[10px] uppercase font-black tracking-widest"
                                     onClick={() => document.getElementById('logo-upload').click()}
                                   >
                                     <Plus className="w-3 h-3 mr-2" />
                                     Cargar Archivo (JPG/PNG)
                                   </Button>
                                   <Button 
                                     variant="outline" 
                                     className="h-10 text-[10px] border-white/10 text-slate-500 uppercase font-black tracking-widest hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/20"
                                     onClick={() => setConfig({ ...config, logo: "" })}
                                   >
                                      Borrar
                                   </Button>
                                </div>
                                <Input 
                                  className="bg-black/40 border-white/10 h-10 text-xs" 
                                  placeholder="O pega una URL externa: https://..." 
                                  value={config.logo || ""} 
                                  onChange={(e) => setConfig({ ...config, logo: e.target.value })} 
                                />
                             </div>
                          </div>
                       </div>

                    <div className="space-y-4 pt-4">
                       <Label className="text-[10px] font-black uppercase text-slate-500">Esquema de Color Primario</Label>
                       <div className="flex items-center gap-6 bg-white/5 p-4 rounded-3xl border border-white/5">
                          <div className="relative">
                             <input type="color" className="w-14 h-14 bg-transparent border-none cursor-pointer" value={config.primary_color} onChange={(e) => setConfig({ ...config, primary_color: e.target.value })} />
                             <div className="absolute inset-2 rounded-xl pointer-events-none" style={{ backgroundColor: config.primary_color }} />
                          </div>
                          <div className="flex-1">
                             <Input className="bg-transparent border-none p-0 text-xl font-black text-white outline-none focus:ring-0" value={config.primary_color} onChange={(e) => setConfig({ ...config, primary_color: e.target.value })} />
                             <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-1">Este color definirá botones, barras y resaltes.</p>
                          </div>
                       </div>
                    </div>
                  </div>
               </CardGlass>
             )}
             {activePane === 'features' && (
               <CardGlass title="Módulos del Sistema" subtitle="Activa o desactiva funcionalidades avanzadas según tu modelo de negocio.">
                  <div className="space-y-6 pt-6">
                    <div className="p-6 bg-white/5 rounded-3xl border border-white/10 flex items-center justify-between group hover:bg-white/[0.07] transition-all">
                       <div className="flex items-center gap-5">
                          <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center text-orange-500"><Beef className="w-6 h-6" /></div>
                          <div>
                             <h4 className="text-sm font-black text-white uppercase tracking-tight">Personalizador (Crea Tu Taco)</h4>
                             <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Permite a los clientes armar sus platos paso a paso.</p>
                          </div>
                       </div>
                       <Switch 
                         checked={config.features?.custom_taco !== false} 
                         onCheckedChange={(val) => setConfig({ ...config, features: { ...config.features, custom_taco: val } })} 
                       />
                    </div>

                    <div className="p-6 bg-white/5 rounded-3xl border border-white/10 flex items-center justify-between group hover:bg-white/[0.07] transition-all">
                       <div className="flex items-center gap-5">
                          <div className="w-12 h-12 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-500"><Store className="w-6 h-6" /></div>
                          <div>
                             <h4 className="text-sm font-black text-white uppercase tracking-tight">Integraciones Externas</h4>
                             <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Conexión con Glovo, UberEats y Delivery Hub.</p>
                          </div>
                       </div>
                       <Switch 
                         checked={config.features?.integrations === true} 
                         onCheckedChange={(val) => setConfig({ ...config, features: { ...config.features, integrations: val } })} 
                       />
                    </div>
                    <div className="p-6 bg-white/5 rounded-3xl border border-white/10 flex items-center justify-between group hover:bg-white/[0.07] transition-all">
                       <div className="flex items-center gap-5">
                          <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-500"><Shield className="w-6 h-6" /></div>
                          <div>
                             <h4 className="text-sm font-black text-white uppercase tracking-tight">Fiscalidad (VeriFactu / TicketBAI)</h4>
                             <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Módulo fiscal para cumplimiento normativo (España).</p>
                          </div>
                       </div>
                       <Switch 
                         checked={!!config.features?.verifactu} 
                         onCheckedChange={(val) => setConfig({ ...config, features: { ...config.features, verifactu: val } })}
                       />
                    </div>
                  </div>
               </CardGlass>
             )}


             {activePane === 'printing' && (
               <CardGlass title="Hub de Impresión Local" subtitle="Configura la conectividad de red para tus tickeras y comandas.">
                  <div className="space-y-8 pt-6">
                    <div className="p-8 bg-gradient-to-br from-indigo-500/10 to-transparent border border-white/5 rounded-[40px] space-y-6">
                       <div className="flex items-center gap-5">
                          <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center text-indigo-400"><Monitor className="w-6 h-6" /></div>
                          <div>
                             <h4 className="text-sm font-black text-white uppercase tracking-tight">Servidor de Comandas</h4>
                             <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">URL del ordenador con el Agente de Impresión</p>
                          </div>
                       </div>
                       <Input className="bg-black/50 border-white/10 h-14 text-indigo-400 font-mono text-center text-lg" placeholder="http://192.168.1.XX:8001" defaultValue={printService.getPrinterServerUrl() || ""} onChange={(e) => printService.setPrinterServerUrl(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-4 text-slate-500 bg-white/5 p-6 rounded-[30px] border border-dashed border-white/10">
                       <Printer size={30} className="opacity-20 flex-shrink-0" />
                       <p className="text-[10px] italic leading-relaxed">"Recomendamos usar una IP estática en el servidor de impresión para evitar desconexiones accidentales al reiniciar el router."</p>
                    </div>
                  </div>
               </CardGlass>
             )}

          </div>

        </div>
      </div>
    </AdminLayout>
  );
}
